import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, ElementRef, ViewChild } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { DataEnvService } from 'src/app/shared/sl-data/data-env.service';
import { SlPlanningService } from 'src/app/shared/sl-planning/sl-planning.service';
import { Router } from '@angular/router';
import { DateUtilsService } from 'src/app/services/date-utils.service';

export interface CsvOrderRow {
  lineNumber: number;
  data: any;
  errors: string[];
  isValid: boolean;
}

export interface CsvPreviewResult {
  totalOrders: number;
  totalItems: number;
  rows: CsvOrderRow[];
  hasErrors: boolean;
  rowsWithErrors: CsvOrderRow[];
  orders: any[];
}

@Component({
  selector: 'app-order-upload',
  templateUrl: './order-upload.component.html',
  styleUrls: ['./order-upload.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderUploadComponent implements OnInit {
    

  selectedFile: File | null = null;
  previewResult: CsvPreviewResult | null = null;
  isProcessing = false;
  uploadEnabled = false;
  public requestInProg$ = new BehaviorSubject(false);
  // Reference to the #fileInput element in the template
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  constructor(private cdr: ChangeDetectorRef, private dataEnv: DataEnvService, private planning: SlPlanningService, private utilities:UtilitiesService, private router: Router, private dateUtils: DateUtilsService) { }

  async ngOnInit(): Promise<void> {
    await this.dataEnv.setComFromCurrentUser();
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      this.selectedFile = file;
      this.cdr.detectChanges();
      this.processFile();
    } else {
      alert('Please select a valid CSV file');
    }
  }

  async processFile(): Promise<void> {
    if (!this.selectedFile) return;

    this.isProcessing = true;
    
    try {
      // Read the file content
      const text = await this.readFileAsText(this.selectedFile);
      const lines = text.split('\n').filter(line => line.trim() !== '');
      
      // Parse CSV and validate
      const rows: CsvOrderRow[] = [];
      let totalOrders = 0;
      let totalItems = 0;
      let hasErrors = false;

      // Skip header row and process data rows
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i];
        const columns = this.parseCsvLine(line);
        if(columns.every(c=>c==='')){ continue;}//Ignore empty lines
        const orderRow = this.parseOrderRow(columns, i + 1);
        
        if (!orderRow.isValid) {
          hasErrors = true;
        }

        // Count orders and items (dummy logic)
        if (orderRow.isValid) {
          totalOrders++;
          totalItems += orderRow.data.quantity; // Assuming column 3 contains item count
        }

        rows.push(orderRow);
      }
      const orders = this.buildOrderFromRows(rows);
      this.previewResult = {
        totalOrders: orders.length,
        totalItems,
        rows,
        rowsWithErrors: rows.filter(r=>!r.isValid),
        hasErrors,
        orders
      };

      this.uploadEnabled = !hasErrors;
      this.cdr.detectChanges();
      
    } catch (error) {
      console.error('Error processing file:', error);
      alert('Error processing file. Please try again.');
    } finally {
      this.isProcessing = false;
      this.cdr.detectChanges();
    }
  }

  private readFileAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = (e) => reject(e);
      reader.readAsText(file);
    });
  }

  private parseCsvLine(line: string): string[] {
    // Simple CSV parser - handles basic comma-separated values
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    result.push(current.trim());
    return result;
  }

  private parseOrderRow(columns: string[], lineNumber: number): CsvOrderRow {
    const errors: string[] = [];
    const orderLineData: any = {}
    
    // Dummy validation rules
    if (columns.length < 24) {
      errors.push(`Insufficient columns. Expected at least 24 columns but found ${columns.length}. Line ${lineNumber}`);
    }
    
    orderLineData.dealerName = columns[0]?.trim();
    if (!orderLineData.dealerName) {
      errors.push(`Dealer name is required. Line ${lineNumber}`);
    }
    
    orderLineData.orderNumber = columns[1]?.trim();
    if (!orderLineData.orderNumber) {
      errors.push(`Order number is required. Line ${lineNumber}`);
    }
    
    orderLineData.invoiceNumber = columns[2]?.trim();
    if (!orderLineData.invoiceNumber) {
      errors.push(`Invoice number is required. Line ${lineNumber}`);
    }
    
    orderLineData.invoiceDate = columns[3]?.trim();
    if (!orderLineData.invoiceDate) {
      errors.push(`Invoice date is required. Line ${lineNumber}`);
    }
    orderLineData.invoiceDate = this.dateUtils.dateToISO(new Date(orderLineData.invoiceDate));

    orderLineData.shippingDate = columns[4]?.trim();
    if (!orderLineData.shippingDate) {
      errors.push(`Shipping date is required. Line ${lineNumber}`);
    }
    orderLineData.shippingDate = this.dateUtils.dateToISO(new Date(orderLineData.shippingDate));

    orderLineData.firstName = columns[5]?.trim();
    if (!orderLineData.firstName) {
      errors.push(`Customer first name is required. Line ${lineNumber}`);
    }

    orderLineData.lastName = columns[6]?.trim();
    if (!orderLineData.lastName) {
      errors.push(`Customer last name is required. Line ${lineNumber}`);
    }
    
    orderLineData.phoneNumber = columns[7]?.trim();
    if (!orderLineData.phoneNumber) {//Validate phone number
      errors.push(`Customer phone number is required. Line ${lineNumber}`);
    }

    orderLineData.streetName = columns[8]?.trim();
    if (!orderLineData.streetName) {
      errors.push(`Street name is required. Line ${lineNumber}`);
    }

    orderLineData.streetNumber = columns[9]?.trim();
    if (!orderLineData.streetNumber) {
      errors.push(`Street number is required. Line ${lineNumber}`);
    }

    orderLineData.state = columns[10]?.trim();
    if (!orderLineData.state) {
      errors.push(`State is required. Line ${lineNumber}`);
    }

    orderLineData.postalCode = columns[11]?.trim();
    if (!orderLineData.postalCode) {
      errors.push(`Postal code is required. Line ${lineNumber}`);
    }

    orderLineData.city = columns[12]?.trim();
    if (!orderLineData.city) {
      errors.push(`City is required. Line ${lineNumber}`);
    }

    orderLineData.floor = columns[13]?.trim();
    orderLineData.apartment = columns[14]?.trim();

    orderLineData.sku = columns[15]?.trim();
    if (!orderLineData.sku) {
      errors.push(`Item SKU is required. Line ${lineNumber}`);
    }

    orderLineData.category = columns[16]?.trim();
    if (!orderLineData.category) {
      errors.push(`Item Category is required. Line ${lineNumber}`);
    }

    orderLineData.description = columns[17]?.trim();

    orderLineData.quantity = parseInt(columns[18] || '0');
    if (isNaN(orderLineData.quantity) || orderLineData.quantity <= 0) {
      errors.push(`Valid item quantity is required. Line ${lineNumber}`);
    }

    orderLineData.serviceType = columns[19]?.trim();
    if (!orderLineData.serviceType) {
      errors.push(`Service type is required. Line ${lineNumber}`);
    }

    orderLineData.serialNumber = columns[20]?.trim();
    orderLineData.vip = columns[21]?.trim().toLowerCase() === 'true' || columns[21]?.trim() === '0';
    orderLineData.serviceDescription = columns[22]?.trim();
    orderLineData.note = columns[23]?.trim();
        
    return {data: orderLineData, errors, isValid: errors.length === 0, lineNumber};
  }

  async onUpload(): Promise<void> {
    if (!this.uploadEnabled || !this.previewResult) {
      return;
    }

    console.log('Uploading orders:', this.previewResult.orders);

    try{
      this.requestInProg$.next(true);
      await this.planning.updateOrders(this.previewResult.orders);
      this.utilities.notifySuccess('Orders created successfully.');
      this.router.navigate(['/orders']);
    }
    catch(err){
        if(err.details){
            this.utilities.notifyError(err.details);
            return;
        }
        this.utilities.notifyError('Unexpected error creating orders. Please contact system admin');
    }
    finally{
      this.requestInProg$.next(false);
    }
  }

  clearFile(): void {
    this.fileInput.nativeElement.value = '';
    this.selectedFile = null;
    this.previewResult = null;
    this.uploadEnabled = false;
    this.isProcessing = false;
    this.cdr.detectChanges();
  }

  groupItemPropertiesToRender(data: any): any {
    if (!data) return {};
    const {apartment, floor, city, state, streetName, streetNumber, postalCode, category, description, sku, dealerName, firstName, lastName, orderNumber, invoiceNumber, invoiceDate, shippingDate, vip, serialNumber, serviceDescription, ...rest} = data;
    const apartmentData = floor && apartment ? `, floor/apartment: ${floor}/${apartment}` : floor ? `, floor: ${floor}` : apartment ? `, apartment: ${apartment}` : '';
    const itemDetails = `SKU: ${sku}, Category: ${category}, ${(serialNumber ? 'Serial Number: ' + serialNumber + ', ': '')}Description: ${description.slice(0, 20)}${(serviceDescription ? ', Service Description: ' + serviceDescription : '')}`;
    const customerName = `${firstName} ${lastName}${(vip ? ' (VIP)' : '') }`;
    const order = `Order # ${orderNumber}, Invoice # ${invoiceNumber}, Invoice Date: ${invoiceDate}, Shipping Date: ${shippingDate}`;
    return {
      "Order": order, 
      "Item Details": itemDetails, 
      "Dealer Name": dealerName, 
      "Customer Name": customerName, 
      address: `${streetName} #${streetNumber}, ${city}, ${state} ${postalCode} ${apartmentData}`, 
      ...rest};
  }

  private customerFromRow = (item) => {
    return {
      firstName: item.firstName,
      lastName: item.lastName,
      fullName: `${item.firstName} ${item.lastName}`,
      phone: item.phoneNumber
    };
  }
  
  private itemFromRow = (item) => {
    return {
      id: -1,
      quantity: item.quantity,
      sku: item.sku,
      description: item.description,
      serialNumber: item.serialNumber,
      services: [this.serviceFromRow(item)],
    };
  }

  private serviceFromRow = (item) => {
    return {
      id: -1,
      name: item.serviceType,
      description: item.serviceDescription,
      isInstall: item.serviceType.toLowerCase() === 'install',
      job:{
        id: -1
      }
    };
  }

  private addressFromRow = (item) => {
    return {
      streetName: item.streetName,
      streetNumber: item.streetNumber,
      city: item.city,
      postalCode: item.postalCode,
      state: item.state,
      apartment: item.apartment,
      floor: item.floor
    } 
  }
  private orderFromRow = (item) => {
    const note = item.note.trim() ? {
      note: {
        text: item.note
      }
    } : {}; 
    return {
      orderNumber: item.orderNumber,
      serviceDate: item.shippingDate,
      items: [this.itemFromRow(item)],
      notes: note,
      address: this.addressFromRow(item),
      customer: this.customerFromRow(item),
      dealerName: item.dealerName,
      vip: item.vip,
      openingTime: '7:30',
      closingTime: '17:30',
      jobs: [
        {
          id: -1,
          serviceTime: 30,
          thirdMan: false,
          preferredStart: '7:30',
          preferredEnd: '17:30',
          coordinated: false,
          sequence: 1
        }
      ]
    };
  }

  private buildOrderFromRows(rows: CsvOrderRow[]){
    const orders = rows.reduce((acc: any[], itemRow: CsvOrderRow)=>{
      const item = itemRow.data;
      let existingOrder = acc.find(o=>o.orderNumber === item.orderNumber)
      if(!existingOrder){
        //Adding a new order to the list
        acc.push(this.orderFromRow(item));
      }
      else{
        //Adding items to an existing order
        const existingItem = existingOrder.items.find(i=>i.sku === item.sku);
        if(!existingItem){
          existingOrder.items.push(this.itemFromRow(item));
        }
        else{
          existingItem.services.push(this.serviceFromRow(item));
        }
      }
      return acc;
    }, []);

    return orders;
  }

  originalOrder = () => 0;
} 